import _startCase from "lodash/startCase";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Dropdown, Grid, GridColumn } from "semantic-ui-react";

import { DealersAndLocationsDropdown, ExportDataButton, NavPortal, ResetFiltersButton, SearchFilter, useCan } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { typedQueryClient, usePermanentRealTimeUser, usePrevious } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { LeadTypeFilter, LeadsExportModal } from "modules/LeadsDashboard/components";
import { useDepartmentOptions, useExportModal, useSystemTagOptions } from "modules/LeadsDashboard/hooks";
import { exportLeadsSnoozed, getDefaultSnoozedFilters, useLeadsSnoozedFilters, useSnoozedFilterHandlers } from "modules/LeadsDashboard/Leads/SnoozedLeads/hooks";
import { LeadsQueryKeys } from "modules/LeadsDashboard/queryKeys";
import { LEADS_SNOOZED_STORAGE_KEYS } from "modules/LeadsDashboard/storageKeys";
import { getPreference } from "utils/common";

export const SnoozedLeadsFilters = () => {
  const { t } = useTranslation();

  const { selectedLocation } = useSelectedLocation();
  const { departmentOptions } = useDepartmentOptions();
  const { systemTagOptions } = useSystemTagOptions();
  const previousLocation = usePrevious(selectedLocation?.id);

  const { user } = usePermanentRealTimeUser();
  const canExportLeads = useCan(ACL_ACTIVITIES.Export, ACL_RESOURCES.LeadsSnoozed);
  const filters = useLeadsSnoozedFilters();
  const {
    handleTagsChange,
    handleSearchChange,
    handleResetFilters,
    handleDepartmentChange,
    handleInterventionsChange,
    handleDealerLocationChange,
    handleCustomerApprovedChange
  } = useSnoozedFilterHandlers();
  const { handleCloseExportModal, handleOpenExportModal, showExportModal } = useExportModal();
  const { search_term, dealer_ids, location_ids, tags, customer_approved, interventions, snooze_department_id } = filters;

  useEffect(() => {
    if (!previousLocation && selectedLocation && !getPreference<boolean>(LEADS_SNOOZED_STORAGE_KEYS.LocationPreference, false))
      typedQueryClient.setQueryData(LeadsQueryKeys.snoozedFilters, {
        ...getDefaultSnoozedFilters(selectedLocation),
        location_ids: [selectedLocation.id]
      });
  }, [previousLocation, selectedLocation]);

  return (
    <>
      <NavPortal>
        <SearchFilter value={search_term} handleSearchChange={handleSearchChange} />
      </NavPortal>

      <div className="toolbar-container">
        <Grid columns={16} className="toolbar-grid">
          <GridColumn width={2} className="no-left-gutter right-gutter">
            <LeadTypeFilter />
          </GridColumn>

          <GridColumn width={2} className="left-gutter right-gutter">
            <Dropdown
              fluid
              clearable
              selection
              name="tag"
              defaultValue={snooze_department_id ?? undefined}
              selectOnBlur={false}
              options={departmentOptions}
              placeholder={t("v8_department", "Department")}
              onChange={(_, { value }) => handleDepartmentChange(value as number)}
            />
          </GridColumn>

          <GridColumn width={4} className="left-gutter right-gutter">
            <DealersAndLocationsDropdown
              values={{ dealerIds: dealer_ids, locationIds: location_ids }}
              handleOnChange={({ dealerIds, locationIds }) => handleDealerLocationChange({ dealerIds, locationIds })}
              allowSelectAllDealers={false}
            />
          </GridColumn>
          <GridColumn width={3} className="left-gutter right-gutter">
            <Dropdown
              fluid
              selection
              clearable
              name="tag"
              multiple
              value={tags || []}
              selectOnBlur={false}
              options={systemTagOptions}
              placeholder={t("v8_tags", "Tags")}
              onChange={(_, { value }) => handleTagsChange(value as number[])}
            />
          </GridColumn>
          <GridColumn width={5} className="left-gutter right-gutter flex-align-justify-center">
            <div className="filters-checkboxes-container">
              <div className="filters-checkbox-container">
                <Checkbox toggle name="customer_approved" onChange={handleCustomerApprovedChange} checked={customer_approved} />
                <span className="no-wrap-text"> {_startCase(t("v8_customer_approved", "Customer approved"))}</span>
              </div>
              <div className="filters-checkbox-container">
                <Checkbox toggle name="intervention" onChange={handleInterventionsChange} checked={interventions} />
                <span className="no-wrap-text"> {t("v8_interventions", "Interventions")}</span>
              </div>
            </div>
          </GridColumn>
        </Grid>

        <div className="toolbar-reset-export-buttons-container">
          <ResetFiltersButton handleReset={handleResetFilters} />
          {user?.csv_download && canExportLeads && <ExportDataButton handleOnClick={handleOpenExportModal} disabled={!dealer_ids.length && !location_ids.length} />}
        </div>
      </div>

      <LeadsExportModal open={showExportModal} onClose={handleCloseExportModal} onExport={() => exportLeadsSnoozed(filters)} />
    </>
  );
};
